import { i18nKeyForError, parseApiError, TagBadge } from '@features/allocation';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormField, Input, Modal, Switch, useToast } from '@ntconsult/aluma-react';
import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSaveAllocationTag } from '../hooks/useSaveAllocationTag';
import type { AllocationTag } from '../types/AllocationTag';
import {
  type AllocationTagFormValues,
  allocationTagFormSchema,
  TAG_COLOR_PATTERN,
  TAG_NAME_MAX_LENGTH,
} from './AllocationTagForm.schema';

/** O padrão do legado (`#FFFFFF`) some no fundo claro; a cor nova começa visível. */
const DEFAULT_COLOR = '#00BCD4';

type AllocationTagModalProps = {
  open: boolean;
  onClose: () => void;
  /** `null` = nova tag. */
  tag: AllocationTag | null;
};

/** O `<input type="color">` só aceita `#rrggbb`: `#abc` vira `#aabbcc`. */
function toPickerValue(color: string): string {
  if (!TAG_COLOR_PATTERN.test(color)) return DEFAULT_COLOR.toLowerCase();
  const hex = color.slice(1);
  const full = hex.length === 3 ? [...hex].map((digit) => digit + digit).join('') : hex;
  return `#${full.toLowerCase()}`;
}

function FormBody({ tag, onClose }: { tag: AllocationTag | null; onClose: () => void }) {
  const { t } = useTranslation(['tagsAlocacao', 'alocacao', 'common']);
  const { toast } = useToast();
  const formId = useId();
  const activeHintId = useId();
  const saveMutation = useSaveAllocationTag();

  const {
    control,
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<AllocationTagFormValues>({
    resolver: zodResolver(allocationTagFormSchema),
    defaultValues: {
      name: tag?.name ?? '',
      color: tag?.color ?? DEFAULT_COLOR,
      active: tag?.active ?? true,
    },
  });

  const name = watch('name').trim();
  const color = watch('color').trim();

  // Códigos do schema viram texto; a mensagem do servidor (`setError`) já vem traduzida.
  const schemaMessages: Record<string, string> = {
    required: t('common:errors.required'),
    tooLong: t('tagsAlocacao:validacao.nomeMaximo', { max: TAG_NAME_MAX_LENGTH }),
    invalidColor: t('tagsAlocacao:validacao.corInvalida'),
  };
  const errorText = (message: string | undefined) =>
    message && (schemaMessages[message] ?? message);

  const onSubmit = handleSubmit((values) => {
    saveMutation.mutate(
      { id: tag?.id, body: values },
      {
        onSuccess: () => {
          toast.success({
            title: tag
              ? t('tagsAlocacao:toasts.atualizarSucesso')
              : t('tagsAlocacao:toasts.criarSucesso'),
          });
          onClose();
        },
        onError: (error) => {
          const apiError = parseApiError(error);
          if (apiError?.code === 'TAG_NAME_TAKEN') {
            setError('name', { message: t('alocacao:erros.tagNameTaken') });
            return;
          }
          toast.danger({
            title: t((apiError ? i18nKeyForError(apiError.code) : 'alocacao:toasts.erro') as never),
          });
        },
      },
    );
  });

  return (
    <>
      <Modal.Body>
        <form id={formId} onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <FormField
            label={t('tagsAlocacao:form.nome')}
            error={errorText(errors.name?.message)}
            required
          >
            <Input {...register('name')} autoFocus maxLength={TAG_NAME_MAX_LENGTH} />
          </FormField>

          <div className="grid grid-cols-[1fr_auto] items-start gap-2">
            <FormField
              label={t('tagsAlocacao:form.cor')}
              hint={t('tagsAlocacao:form.corDica')}
              error={errorText(errors.color?.message)}
              required
            >
              <Input
                {...register('color')}
                maxLength={7}
                className="font-mono uppercase"
                leftIcon={
                  <span
                    aria-hidden="true"
                    className="inline-block h-3 w-3 rounded-full border border-border"
                    style={{ backgroundColor: TAG_COLOR_PATTERN.test(color) ? color : undefined }}
                  />
                }
              />
            </FormField>
            <Controller
              control={control}
              name="color"
              render={({ field }) => (
                <FormField label={t('tagsAlocacao:form.corSeletor')}>
                  <Input
                    type="color"
                    className="w-16 cursor-pointer p-1"
                    value={toPickerValue(field.value.trim())}
                    onChange={(event) => field.onChange(event.target.value.toUpperCase())}
                    onBlur={field.onBlur}
                  />
                </FormField>
              )}
            />
          </div>

          <div className="flex flex-col gap-1">
            <Controller
              control={control}
              name="active"
              render={({ field }) => (
                <Switch
                  label={t('tagsAlocacao:form.ativa')}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-describedby={activeHintId}
                />
              )}
            />
            <p id={activeHintId} className="text-sm text-foreground-muted">
              {t('tagsAlocacao:form.ativaDica')}
            </p>
          </div>

          {tag && tag.allocationCount > 0 && (
            <p className="text-sm text-foreground-muted">
              {t('tagsAlocacao:form.emUso', { count: tag.allocationCount })}
            </p>
          )}

          {name && TAG_COLOR_PATTERN.test(color) && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-muted">{t('tagsAlocacao:form.previa')}</span>
              <TagBadge tag={{ id: tag?.id ?? 0, name, color }} />
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose} disabled={saveMutation.isPending}>
          {t('common:cancel')}
        </Button>
        <Button type="submit" form={formId} variant="primary" loading={saveMutation.isPending}>
          {tag ? t('common:save') : t('tagsAlocacao:form.criar')}
        </Button>
      </Modal.Footer>
    </>
  );
}

export function AllocationTagModal({ open, onClose, tag }: AllocationTagModalProps) {
  const { t } = useTranslation(['tagsAlocacao', 'common']);
  const titleId = useId();

  return (
    <Modal open={open} onClose={onClose} ariaLabelledBy={titleId} size="md">
      <Modal.Header
        titleId={titleId}
        title={tag ? t('tagsAlocacao:form.tituloEditar') : t('tagsAlocacao:form.tituloNova')}
        onClose={onClose}
        dismissLabel={t('common:close')}
      />
      {open && <FormBody key={tag?.id ?? 'nova'} tag={tag} onClose={onClose} />}
    </Modal>
  );
}
